/** @jsx jsx */
import { type ReactNode, memo } from 'react';
import { jsx, css } from '@emotion/react';
// @ts-expect-error
import { LayoutRenderer } from 'pretty-proptypes';
import Markdown from 'react-markdown';
import { Code, CodeBlock } from '@atlaskit/code';
import Lozenge from '@atlaskit/lozenge';
import Link from '@atlaskit/link';
import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  G50,
  G500,
  P50,
  P300,
  P500,
  N20,
  N300,
  R500,
  B50,
} from '@atlaskit/theme/colors';
import { fontFallback } from '@atlaskit/theme/typography';
import { SectionLink } from '@af/design-system-docs-ui';
import { Text } from '@atlaskit/primitives';

const containerStyles = css({
  borderRadius: token('border.radius.200', '8px'),
  margin: `${token('space.400', '32px')} ${token(
    'space.negative.200',
    '-16px',
  )} ${token('space.0', '0px')}`,
  padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
  ':first-of-type:is(div)': {
    marginBlockStart: token('space.0', '0px'),
  },
  ':target': {
    animation: 'highlight 250ms 2.5s ease-out forwards',
    backgroundColor: token('color.background.accent.blue.subtlest', B50),
  },
  '@keyframes highlight': {
    '0%': {
      backgroundColor: token('color.background.accent.blue.subtlest', B50),
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
});

const monospaceFontStack = `'SFMono-Medium', 'SF Mono', 'Segoe UI Mono', 'Roboto Mono', 'Ubuntu Mono', Menlo, Consolas, Courier, monospace`;

const propTitleContainerStyles = xcss({
  borderBlockEndWidth: 'border.width',
  borderBlockEndStyle: 'solid',
  borderBlockEndColor: 'color.border',
  marginBlockEnd: 'space.050',
  paddingBlockEnd: 'space.100',
});

const baseCodeStyles = css({
  // to make precedence deterministic these styles have the extra pseudo class ... wouldn't be otherwise needed
  '&:first-of-type:is(code)': {
    font: token('font.body', fontFallback.body.medium),
    fontFamily: token('font.family.monospace', monospaceFontStack),
    padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
  },
});

const deprecatedStyles = css({ textDecoration: 'line-through' });

const tableStyles = css({
  width: '100%',
  borderCollapse: 'collapse',
  th: {
    textAlign: 'left',
    padding: `${token('space.050', '4px')} ${token(
      'space.200',
      '16px',
    )} ${token('space.050', '4px')} ${token('space.100', '8px')}`,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  td: {
    padding: `${token('space.050', '4px')} ${token('space.050', '4px')} ${token(
      'space.100',
      '8px',
    )}`,
    width: '100%',
  },
  tbody: {
    borderBlockEnd: 'none',
  },
  code: {
    wordBreak: 'normal',
    overflowWrap: 'anywhere',
  },
});

const expanderStyles = css({
  borderRadius: token('border.radius', '3px'),
  border: 0,
  font: token('font.body', fontFallback.body.medium),
  width: 'auto',
  margin: `${token('space.025', '2px')} ${token('space.0', '0px')}`,
  padding: `${token('space.0', '0px')} ${token('space.025', '3px')}`,
  ':hover': {
    cursor: 'pointer',
  },
});

const typeStyles = css({
  backgroundColor: token('color.background.discovery', P50),
  borderRadius: token('border.radius', '3px'),
  color: token('color.text.discovery', P500),
  display: 'inline-block',
  margin: `${token('space.025', '2px')} ${token('space.0', '0px')}`,
  padding: `${token('space.0', '0px')} ${token('space.025', '0.2em')}`,
});

const descriptionStyles = css({
  'p:first-of-type': {
    marginBlockStart: token('space.0', '0px'),
  },
  'p:last-of-type': {
    marginBlockEnd: token('space.0', '0px'),
  },
});

const Type = (props: { children: ReactNode }) => (
  <span css={typeStyles} {...props} />
);

const StringType = (props: { children: ReactNode }) => (
  <span css={[typeStyles, stringType]} {...props} />
);
const Required = (props: { children: ReactNode }) => (
  <span
    css={css({
      color: token('color.text.danger', R500),
    })}
  >
    {props.children}
  </span>
);

const Expander = ({
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  children,
}: {
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    css={expanderStyles}
    style={{
      backgroundColor: isHovered
        ? token('color.background.discovery.hovered', P300)
        : token('color.background.neutral', N20),
      color: token('color.text.subtle', N300),
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </button>
);

const FunctionType = (props: { children: ReactNode }) => <span {...props} />;

const typeMeta = css({
  backgroundColor: token('color.background.neutral', N20),
  color: token('color.text.subtle', N300),
});

const TypeMeta = (props: { children: ReactNode }) => (
  <span css={[typeStyles, typeMeta]} {...props} />
);

const stringType = css({
  backgroundColor: token('color.background.success', G50),
  color: token('color.text.success', G500),
});

export const PropTable = ({
  defaultValue,
  deprecated,
  description,
  name,
  required,
  type,
  typeValue,
  componentDisplayName,
}: {
  name: string;
  required: boolean;
  deprecated: boolean;
  defaultValue: any;
  description: string;
  type: any;
  typeValue: any;
  componentDisplayName?: string;
}) => {
  const titleId =
    componentDisplayName && typeof componentDisplayName === 'string'
      ? `${componentDisplayName}-${name}`
      : name;

  return (
    <div id={titleId} css={containerStyles}>
      <table css={tableStyles}>
        <caption
          css={css({
            textAlign: 'left',
            margin: token('space.0', '0px'),
            fontSize: '1em',
          })}
        >
          <Inline
            xcss={propTitleContainerStyles}
            space="space.200"
            alignBlock="center"
          >
            <SectionLink id={titleId} depth={4} heading={name}>
              <Code css={[baseCodeStyles, deprecated && deprecatedStyles]}>
                {name}
              </Code>
            </SectionLink>
            {required && <Lozenge appearance="removed">Required</Lozenge>}
            {deprecated && <Lozenge appearance="moved">Deprecated</Lozenge>}
          </Inline>
        </caption>
        <tbody>
          <tr>
            <th scope="row">Description</th>
            <td>
              <div css={descriptionStyles}>
                {description ? (
                  <Markdown
                    components={{
                      a: ({ children, href }) => (
                        <Link href={href!}>{children}</Link>
                      ),
                      code: ({ inline, children }) => {
                        return inline ? (
                          <Code>{children}</Code>
                        ) : (
                          <CodeBlock
                            text={children.join('\n')}
                            language="TSX"
                            showLineNumbers={false}
                          />
                        );
                      },
                    }}
                    transformLinkUri={(uri) => {
                      /**
                       * Transform absolute atlassian.design URLs to relative links,
                       * so the router can handle them and not trigger page reloads.
                       */
                      return uri.replace(
                        /^https:\/\/(www\.)*atlassian.design/g,
                        '',
                      );
                    }}
                  >
                    {/* Deprecated tags are already extracted into a lozenge above using pretty-prop-types */}
                    {description.replace('@deprecated', '')}
                  </Markdown>
                ) : (
                  <Text color="color.text.subtle">
                    <i>No description.</i>
                  </Text>
                )}
              </div>
            </td>
          </tr>
          {defaultValue !== undefined && (
            <tr>
              <th scope="row">Default</th>
              <td>
                <Code>{defaultValue}</Code>
              </td>
            </tr>
          )}
          <tr>
            <th scope="row">Type</th>
            <td
              css={css({
                display: 'flex',
                flexDirection: 'column',
              })}
            >
              <span>
                <Code>{type}</Code>
              </span>
              <span>{typeValue}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const PropsLayoutRenderer = memo((props) => (
  <LayoutRenderer
    requiredPropsFirst
    {...props}
    renderType={({ components, typeValue, ...type }: any) => (
      <PropTable
        {...type}
        typeValue={
          <components.PropType
            typeValue={typeValue}
            components={{
              ...components,
              Type,
              TypeMeta,
              StringType,
              FunctionType,
              Required,
              Expander,
            }}
          />
        }
      />
    )}
  />
));

export default PropsLayoutRenderer;
